import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import config from "../../config/config";

const PageManager = () => {
  const [pages, setPages] = useState([]);
  const [allPagesForInheritance, setAllPagesForInheritance] = useState([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    content: "",
    parent_id: "",
    is_active: true,
    meta_description: "",
    gallery_inheritance_type: "parent",
    gallery_inheritance_id: "",
  });
  const [pageImages, setPageImages] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [galleryInheritanceInfo, setGalleryInheritanceInfo] = useState(null);

  // Fetch pages on component mount
  useEffect(() => {
    fetchPages();
    fetchPagesForInheritance();
  }, []);

  // Fetch all pages
  const fetchPages = async () => {
    try {
      setLoading(true);
      console.log("PageManager: Fetching pages from API...");
      const apiUrl = config.getAdminApiUrl("get_pages.php");
      console.log("PageManager: API URL:", apiUrl);
      
      const response = await fetch(apiUrl, {
        headers: {
          "X-API-Key": "ncg_admin_2024",
        },
      });
      const data = await response.json();
      
      console.log("PageManager: API Response:", data);
      
      if (data.success) {
        console.log("PageManager: Setting", data.pages.length, "pages");
        setPages(data.pages);
      } else {
        console.error("PageManager: API Error:", data.message);
      }
    } catch (error) {
      console.error("Error fetching pages:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all pages for gallery inheritance dropdown
  const fetchPagesForInheritance = async () => {
    try {
      const apiUrl = config.getAdminApiUrl("get_pages_for_gallery_inheritance.php");
      const response = await fetch(apiUrl, {
        headers: {
          "X-API-Key": "ncg_admin_2024",
        },
      });
      const data = await response.json();
      
      if (data.success) {
        setAllPagesForInheritance(data.pages);
      }
    } catch (error) {
      console.error("Error fetching pages for inheritance:", error);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    // Auto-generate slug from title
    if (name === "title") {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9 -]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
      setFormData((prev) => ({ ...prev, slug }));
    }

    // Handle gallery inheritance type change
    if (name === "gallery_inheritance_type") {
      if (value === "custom") {
        setFormData((prev) => ({ ...prev, gallery_inheritance_id: "" }));
      } else {
        setFormData((prev) => ({ ...prev, gallery_inheritance_id: "" }));
      }
    }
  };

  // Fetch page images
  const fetchPageImages = async (pageId) => {
    try {
      console.log("PageManager: Fetching images for page ID:", pageId);
      const apiUrl = config.getAdminApiUrl(`get_page_images.php?page_id=${pageId}`);
      console.log("PageManager: Images API URL:", apiUrl);
      
      const response = await fetch(apiUrl, {
        headers: {
          "X-API-Key": "ncg_admin_2024",
        },
      });
      const data = await response.json();

      console.log("PageManager: Images API Response:", data);

      if (data.success) {
        console.log("PageManager: Setting", data.data.length, "images");
        console.log("PageManager: Images data:", data.data);
        setPageImages(data.data);
        setGalleryInheritanceInfo(data.gallery_inheritance);
      } else {
        console.error("PageManager: Images API Error:", data.message);
      }
    } catch (error) {
      console.error("Error fetching page images:", error);
    }
  };

  const [imageUploadModal, setImageUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageFormData, setImageFormData] = useState({
    image_title: "",
    image_description: "",
  });

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && selectedPageId) {
      setSelectedFile(file);
      setImageFormData({
        image_title: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
        image_description: "",
      });
      setImageUploadModal(true);
    }
  };

  // Handle image upload
  const handleImageUpload = async () => {
    if (!selectedFile || !selectedPageId) return;

    try {
      setImageUploading(true);
      const formData = new FormData();
      formData.append("image", selectedFile);
      formData.append("page_id", selectedPageId);
      formData.append("image_title", imageFormData.image_title);
      formData.append("image_description", imageFormData.image_description);

      const response = await fetch(config.getAdminApiUrl("upload_page_image.php"), {
        method: "POST",
        headers: {
          "X-API-Key": "ncg_admin_2024",
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        fetchPageImages(selectedPageId);
        setImageUploadModal(false);
        setSelectedFile(null);
        setImageFormData({ image_title: "", image_description: "" });
        // Reset file input
        const fileInput = document.getElementById("image-upload");
        if (fileInput) fileInput.value = "";
      } else {
        alert("Failed to upload image: " + data.message);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Error uploading image");
    } finally {
      setImageUploading(false);
    }
  };

  // Handle image deletion
  const handleDeleteImage = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      const response = await fetch(config.getAdminApiUrl("delete_page_image.php"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify({ id: imageId }),
      });

      const data = await response.json();

      if (data.success) {
        fetchPageImages(selectedPageId);
      } else {
        alert("Failed to delete image: " + data.message);
      }
    } catch (error) {
      console.error("Error deleting image:", error);
      alert("Error deleting image");
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const url = editingPage
        ? config.getAdminApiUrl("update_page.php")
        : config.getAdminApiUrl("create_page.php");

      const requestData = editingPage
        ? { ...formData, id: editingPage.id }
        : formData;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (data.success) {
        fetchPages();
        resetForm();
        setShowAddModal(false);
        alert(
          editingPage
            ? "Page updated successfully!"
            : "Page created successfully!"
        );
      } else {
        alert("Error: " + data.message);
      }
    } catch (error) {
      console.error("Error saving page:", error);
      alert("Error saving page");
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: "",
      slug: "",
      content: "",
      is_active: true,
      meta_description: "",
      gallery_inheritance_type: "parent",
      gallery_inheritance_id: "",
    });
    setEditingPage(null);
    setSelectedPageId(null);
    setPageImages([]);
    setGalleryInheritanceInfo(null);
  };

  // Handle edit page
  const handleEdit = (page) => {
    setFormData({
      title: page.title,
      slug: page.slug,
      content: page.content || "",
      is_active: page.is_active,
      meta_description: page.meta_description || "",
      gallery_inheritance_type: page.gallery_inheritance_type || "parent",
      gallery_inheritance_id: page.gallery_inheritance_id || "",
    });
    setEditingPage(page);
    setSelectedPageId(page.id);
    setShowAddModal(true);
    fetchPageImages(page.id);
  };

  // Handle delete page
  const handleDelete = async (pageId) => {
    if (!window.confirm("Are you sure you want to delete this page?")) return;

    try {
      setLoading(true);
      const response = await fetch(config.getAdminApiUrl("delete_page.php"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify({ id: pageId }),
      });

      const data = await response.json();

      if (data.success) {
        fetchPages();
        alert("Page deleted successfully!");
      } else {
        alert("Error deleting page: " + data.message);
      }
    } catch (error) {
      console.error("Error deleting page:", error);
      alert("Error deleting page");
    } finally {
      setLoading(false);
    }
  };

  // Check if page can have its own images
  const canHaveOwnImages = () => {
    return formData.gallery_inheritance_type === "none" || 
           formData.gallery_inheritance_type === "parent";
  };

  // Get gallery source display text
  const getGallerySourceText = () => {
    if (galleryInheritanceInfo) {
      switch (galleryInheritanceInfo.type) {
        case "custom":
          return galleryInheritanceInfo.source_page ? 
            `Inherited from: ${galleryInheritanceInfo.source_page.page_title}` : 
            "Inherited from custom page";
        case "parent":
          return "Using parent page gallery";
        case "none":
          return "Using own gallery";
        default:
          return "Gallery source unknown";
      }
    }
    return "";
  };

  return (
    <div className="page-manager">
      <div className="manager-header">
        <h2>Page Management</h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
        >
          <i className="fas fa-plus me-2"></i>
          Add New Page
        </button>
      </div>

      {/* Pages Table */}
      <div className="manager-content">
        {loading ? (
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i> Loading...
          </div>
        ) : (
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Slug</th>
                  <th>Gallery Source</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pages.length > 0 ? (
                  pages.map((page) => (
                    <tr key={page.id}>
                      <td>{page.title}</td>
                      <td>
                        <code>{page.slug}</code>
                      </td>
                      <td>
                        <span className="gallery-source-badge">
                          {page.gallery_source_name || "Own Gallery"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`status-badge ${
                            page.is_active ? "active" : "inactive"
                          }`}
                        >
                          {page.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>{new Date(page.updated_at).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEdit(page)}
                            title="Edit"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(page.id)}
                            title="Delete"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No pages found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <div className="modal-header">
              <h3>{editingPage ? "Edit Page" : "Add New Page"}</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Page Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter page title"
                  />
                </div>

                <div className="form-group">
                  <label>Page Slug *</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    required
                    placeholder="page-url-slug"
                  />
                  <small>URL-friendly version of the title</small>
                </div>

                <div className="form-group">
                  <label>Meta Description</label>
                  <input
                    type="text"
                    name="meta_description"
                    value={formData.meta_description}
                    onChange={handleInputChange}
                    placeholder="SEO meta description"
                  />
                </div>

                <div className="form-group">
                  <label>Gallery Inheritance *</label>
                  <select
                    name="gallery_inheritance_type"
                    value={formData.gallery_inheritance_type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="parent">Parent Page Gallery (Default)</option>
                    <option value="none">Own Gallery</option>
                    <option value="custom">Custom Page Gallery</option>
                  </select>
                  <small>Select where this page gets its images from</small>
                </div>

                {formData.gallery_inheritance_type === "custom" && (
                  <div className="form-group">
                    <label>Select Page to Inherit Gallery From *</label>
                    <select
                      name="gallery_inheritance_id"
                      value={formData.gallery_inheritance_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select a page...</option>
                      {allPagesForInheritance.map((page) => (
                        <option key={page.id} value={page.id}>
                          {page.title}
                        </option>
                      ))}
                    </select>
                    <small>Choose which page's gallery this page should use</small>
                  </div>
                )}

                <div className="form-group full-width">
                  <label>Page Content</label>
                  <div className="editor-container">
                    <ReactQuill
                      value={formData.content}
                      onChange={(content) =>
                        setFormData({ ...formData, content })
                      }
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, false] }],
                          ["bold", "italic", "underline", "strike"],
                          [{ color: [] }, { background: [] }],
                          [{ list: "ordered" }, { list: "bullet" }],
                          [{ align: [] }],
                          ["link", "image"],
                          ["clean"],
                        ],
                      }}
                      formats={[
                        "header",
                        "bold",
                        "italic",
                        "underline",
                        "strike",
                        "color",
                        "background",
                        "list",
                        "bullet",
                        "align",
                        "link",
                        "image",
                      ]}
                      placeholder="Write your page content here..."
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    <span>Active Page</span>
                  </label>
                </div>

                {/* Page Gallery Section */}
                {selectedPageId && (
                  <div className="form-group full-width">
                    <label>
                      Page Gallery
                      {galleryInheritanceInfo && (
                        <span className="gallery-source-info">
                          {" "}({getGallerySourceText()})
                        </span>
                      )}
                    </label>
                    
                    {canHaveOwnImages() ? (
                      <div className="gallery-section">
                        <div className="gallery-upload">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            disabled={imageUploading || pageImages.length >= 25}
                            style={{ display: "none" }}
                            id="image-upload"
                          />
                          <label
                            htmlFor="image-upload"
                            className={`upload-btn ${
                              imageUploading || pageImages.length >= 25
                                ? "disabled"
                                : ""
                            }`}
                          >
                            {imageUploading ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-plus"></i>
                            )}
                            <span>Upload Image</span>
                          </label>
                          <small>
                            {pageImages.length}/25 images uploaded
                            {pageImages.length >= 25 && " (Maximum reached)"}
                          </small>
                        </div>

                        {pageImages.length > 0 && (
                          <div className="gallery-grid">
                            {console.log("PageManager: Rendering", pageImages.length, "images:", pageImages)}
                            {pageImages.map((image) => {
                              const imageUrl = config.getImageUrl(`page_images/${image.image_filename}`);
                              console.log("PageManager: Image URL for", image.image_filename, ":", imageUrl);
                              return (
                                <div key={image.id} className="gallery-item">
                                  <img
                                    src={imageUrl}
                                    alt={image.image_title || "Gallery image"}
                                    onError={(e) => {
                                      console.error("PageManager: Image failed to load:", imageUrl);
                                      e.target.style.display = "none";
                                    }}
                                    onLoad={() => console.log("PageManager: Image loaded successfully:", imageUrl)}
                                  />
                                  <div className="gallery-item-overlay">
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteImage(image.id)}
                                      className="delete-btn"
                                      title="Delete image"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="gallery-inheritance-notice">
                        <i className="fas fa-info-circle"></i>
                        <span>This page inherits its gallery from another page. Images cannot be uploaded or deleted here.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <i className="fas fa-spinner fa-spin me-2"></i>
                  ) : (
                    <i className="fas fa-save me-2"></i>
                  )}
                  {editingPage ? "Update Page" : "Create Page"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .page-manager {
          padding: 2rem;
          background: #f8f9fa;
          min-height: 100vh;
        }

        .content-header {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }

        .content-header h2 {
          margin: 0 0 0.5rem 0;
          color: #2c3e50;
          font-weight: 600;
        }

        .content-header p {
          margin: 0;
          color: #6c757d;
        }

        .add-page-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          transition: transform 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }

        .add-page-btn:hover {
          transform: translateY(-2px);
        }

        .pages-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1.5rem;
        }

        .page-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .page-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        }

        .page-card-header {
          padding: 1.5rem;
          border-bottom: 1px solid #e9ecef;
        }

        .page-title {
          margin: 0 0 0.5rem 0;
          color: #2c3e50;
          font-weight: 600;
          font-size: 1.1rem;
        }

        .page-slug {
          color: #6c757d;
          font-size: 0.9rem;
          font-family: monospace;
        }

        .page-status {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
          margin-top: 0.5rem;
        }

        .page-status.active {
          background: #d4edda;
          color: #155724;
        }

        .page-status.inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .page-card-body {
          padding: 1.5rem;
        }

        .page-content-preview {
          color: #6c757d;
          font-size: 0.9rem;
          line-height: 1.5;
          margin-bottom: 1rem;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .page-actions {
          display: flex;
          gap: 0.5rem;
        }

        .btn {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 6px;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-primary:hover {
          background: #0056b3;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background: #545b62;
        }

        .btn-danger {
          background: #dc3545;
          color: white;
        }

        .btn-danger:hover {
          background: #c82333;
        }

        .modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          width: 90%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          padding: 1.5rem;
          border-bottom: 1px solid #e9ecef;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h3 {
          margin: 0;
          color: #2c3e50;
          font-weight: 600;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6c757d;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background 0.2s ease;
        }

        .close-btn:hover {
          background: #f8f9fa;
        }

        .modal-body {
          padding: 1.5rem;
        }

        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group label {
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: #2c3e50;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 0.75rem;
          border: 1px solid #ced4da;
          border-radius: 6px;
          font-size: 0.9rem;
          transition: border-color 0.2s ease;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
        }

        .form-group small {
          margin-top: 0.25rem;
          color: #6c757d;
          font-size: 0.8rem;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }

        .checkbox-label input[type="checkbox"] {
          width: auto;
          margin: 0;
        }

        .editor-container {
          border: 1px solid #ced4da;
          border-radius: 6px;
          overflow: hidden;
        }

        .editor-container .ql-editor {
          min-height: 200px;
        }

        .modal-footer {
          padding: 1rem;
          border-top: 1px solid #e9ecef;
          display: flex;
          justify-content: flex-end;
          gap: 0.5rem;
        }

        /* Gallery Styles */
        .gallery-section {
          margin-top: 1rem;
        }

        .gallery-upload {
          margin-bottom: 1rem;
          text-align: center;
        }

        .upload-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: #007bff;
          color: white;
          border: 2px dashed #007bff;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-weight: 500;
        }

        .upload-btn:hover:not(.disabled) {
          background: #0056b3;
          border-color: #0056b3;
        }

        .upload-btn.disabled {
          background: #6c757d;
          border-color: #6c757d;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }

        .gallery-item {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: transform 0.2s ease;
        }

        .gallery-item:hover {
          transform: translateY(-2px);
        }

        .gallery-item img {
          width: 100%;
          height: 120px;
          object-fit: cover;
          display: block;
        }

        .gallery-item-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .gallery-item:hover .gallery-item-overlay {
          opacity: 1;
        }

        .delete-btn {
          background: #dc3545;
          color: white;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .delete-btn:hover {
          background: #c82333;
        }

        /* Gallery Inheritance Styles */
        .gallery-source-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          background: #e3f2fd;
          color: #1976d2;
        }

        .gallery-source-info {
          color: #6c757d;
          font-weight: normal;
          font-size: 0.9rem;
        }

        .gallery-inheritance-notice {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem;
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          border-radius: 6px;
          color: #856404;
          font-size: 0.9rem;
        }

        .gallery-inheritance-notice i {
          color: #f39c12;
          font-size: 1.1rem;
        }

        @media (max-width: 768px) {
          .page-manager {
            padding: 1rem;
          }

          .pages-grid {
            grid-template-columns: 1fr;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .modal-content {
            width: 95%;
            margin: 1rem;
          }
        }

        /* Image Upload Modal Styles */
        .file-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem;
          background: #f8f9fa;
          border-radius: 6px;
          border: 1px solid #e9ecef;
        }

        .file-info i {
          color: #007bff;
          font-size: 1.2rem;
        }

        .file-info span {
          flex: 1;
          font-weight: 500;
        }

        .file-info small {
          color: #6c757d;
        }
      `}</style>

      {/* Image Upload Modal */}
      {imageUploadModal && (
        <div className="modal">
          <div className="modal-content" style={{ maxWidth: "500px" }}>
            <div className="modal-header">
              <h3>Upload Image</h3>
              <button
                type="button"
                className="close-btn"
                onClick={() => {
                  setImageUploadModal(false);
                  setSelectedFile(null);
                  setImageFormData({ image_title: "", image_description: "" });
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Image Title *</label>
                <input
                  type="text"
                  value={imageFormData.image_title}
                  onChange={(e) =>
                    setImageFormData({
                      ...imageFormData,
                      image_title: e.target.value,
                    })
                  }
                  placeholder="Enter image title"
                  required
                />
              </div>
              <div className="form-group">
                <label>Image Description</label>
                <textarea
                  value={imageFormData.image_description}
                  onChange={(e) =>
                    setImageFormData({
                      ...imageFormData,
                      image_description: e.target.value,
                    })
                  }
                  placeholder="Enter image description (optional)"
                  rows="3"
                />
              </div>
              {selectedFile && (
                <div className="form-group">
                  <label>Selected File</label>
                  <div className="file-info">
                    <i className="fas fa-image"></i>
                    <span>{selectedFile.name}</span>
                    <small>
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </small>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setImageUploadModal(false);
                  setSelectedFile(null);
                  setImageFormData({ image_title: "", image_description: "" });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleImageUpload}
                disabled={imageUploading || !imageFormData.image_title}
              >
                {imageUploading ? (
                  <i className="fas fa-spinner fa-spin me-2"></i>
                ) : (
                  <i className="fas fa-upload me-2"></i>
                )}
                Upload Image
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageManager;
